import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash, Funnel } from '@phosphor-icons/react';

const OPERATORS = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'not_contains', label: 'Does Not Contain' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'lt', label: 'Less Than' },
  { value: 'gte', label: 'Greater Than or Equal' },
  { value: 'lte', label: 'Less Than or Equal' },
];

const FilterBuilder = ({ conditions, setConditions, logic, setLogic, fields = [], onApply, onClear }) => {
  const addCondition = () => {
    setConditions([...conditions, { field: fields[0] || '', operator: 'contains', value: '' }]);
  };

  const removeCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index, key, value) => {
    const updated = [...conditions];
    updated[index] = { ...updated[index], [key]: value };
    setConditions(updated);
  };

  const selectClass = "h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring";

  return (
    <div className="space-y-3" data-testid="filter-builder">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Funnel size={16} className="text-primary" />
          <Label className="text-sm font-medium">Advanced Filters</Label>
        </div>
        {conditions.length > 1 && (
          <div className="flex items-center gap-1 bg-secondary rounded-md p-0.5">
            <button
              className={`px-3 py-1 text-xs rounded transition-colors ${logic === 'and' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setLogic('and')}
              data-testid="filter-logic-and"
            >
              AND
            </button>
            <button
              className={`px-3 py-1 text-xs rounded transition-colors ${logic === 'or' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setLogic('or')}
              data-testid="filter-logic-or"
            >
              OR
            </button>
          </div>
        )}
      </div>

      {conditions.map((cond, index) => (
        <div key={index} data-testid={`filter-condition-${index}`}>
          <div className="grid grid-cols-[auto_160px_160px_1fr_36px] items-center gap-2">
            <div className="w-8 text-center shrink-0">
              {index > 0 ? (
                <span className="text-xs text-muted-foreground font-medium">
                  {logic.toUpperCase()}
                </span>
              ) : <span />}
            </div>

            <select
              value={cond.field || ''}
              onChange={(e) => updateCondition(index, 'field', e.target.value)}
              className={selectClass}
              data-testid={`filter-field-${index}`}
            >
              <option value="">Select field</option>
              {fields.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>

            <select
              value={cond.operator}
              onChange={(e) => updateCondition(index, 'operator', e.target.value)}
              className={selectClass}
              data-testid={`filter-operator-${index}`}
            >
              {OPERATORS.map((op) => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>

            <Input
              value={cond.value}
              onChange={(e) => updateCondition(index, 'value', e.target.value)}
              placeholder="Enter value..."
              className="min-w-0"
              data-testid={`filter-value-${index}`}
            />

            <Button
              variant="ghost"
              size="icon"
              onClick={() => removeCondition(index)}
              className="text-destructive shrink-0 h-9 w-9"
              data-testid={`filter-remove-${index}`}
            >
              <Trash size={14} />
            </Button>
          </div>
        </div>
      ))}

      <div className="flex items-center gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={addCondition} data-testid="filter-add-condition">
          <Plus size={14} className="mr-1" /> Add Condition
        </Button>
        {conditions.length > 0 && (
          <>
            <Button size="sm" onClick={onApply} data-testid="filter-apply">
              Apply Filters
            </Button>
            <Button variant="ghost" size="sm" onClick={onClear} data-testid="filter-clear">
              Clear All
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export { FilterBuilder };
